import React from "react";
import { Link } from "react-router-dom";
import { Shield, FileCheck2, Lock, Server, KeyRound, ScrollText, ArrowRight } from "lucide-react";
import EcosystemLogos from "../components/EcosystemLogos";
import SEO, { breadcrumb, faqSchema } from "../components/SEO";

const FAQS = [
  { q: "MetaConnect est-il conforme à l'hébergement HDS ?", a: "Oui. Les données de santé sont hébergées en France auprès d'un hébergeur certifié HDS." },
  { q: "Quels référentiels nationaux MetaConnect prend-il en charge ?", a: "INS qualifiée, DMP / Mon Espace Santé, MSSanté, Pro Santé Connect, e-CPS, ainsi que le respect de la PGSSI-S et du RGPD." },
  { q: "Comment MetaConnect garantit-il la traçabilité des accès ?", a: "Toutes les actions sont journalisées, avec un audit complet des consentements et des échanges de données entre professionnels." },
];

const PILLARS = [
  { icon: <Server size={22} />, title: "Hébergement HDS", desc: "Données de santé hébergées en France auprès d'un hébergeur certifié HDS.", tag: "HDS" },
  { icon: <Shield size={22} />, title: "PGSSI-S", desc: "Politique Générale de Sécurité des Systèmes d'Information de Santé respectée.", tag: "PGSSI-S" },
  { icon: <Lock size={22} />, title: "RGPD", desc: "Privacy by design, registre des traitements, gestion des consentements et droits patient.", tag: "RGPD" },
  { icon: <KeyRound size={22} />, title: "Identification & authentification", desc: "Pro Santé Connect, e-CPS, INS qualifiée. Authentification forte par défaut.", tag: "PSC · INSi" },
  { icon: <FileCheck2 size={22} />, title: "Référentiels nationaux", desc: "Conforme aux référentiels socles : INS, DMP, MSSanté, Mon Espace Santé.", tag: "Référentiels" },
  { icon: <ScrollText size={22} />, title: "Traçabilité & audit", desc: "Journalisation des accès, traçabilité des consentements et des échanges de données.", tag: "Audit" },
];

const CONFORMITE_SCHEMA = [
  breadcrumb([
    { name: "Accueil", url: "/" },
    { name: "Conformité", url: "/conformite" },
  ]),
  faqSchema(FAQS),
];

export default function Conformite() {
  return (
    <div className="bg-[var(--mc-navy-950)]" data-testid="page-conformite">
      <SEO
        title="Conformité e-santé : HDS, RGPD, PGSSI-S et INSi · MetaConnect"
        description="HDS, RGPD, PGSSI-S, INSi, Pro Santé Connect : comment MetaConnect répond aux référentiels nationaux du numérique en santé, avec une traçabilité native."
        canonical="/conformite"
        schema={CONFORMITE_SCHEMA}
      />
      <section className="relative hex-grid pt-32 pb-20 lg:pt-40 lg:pb-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Eyebrow>Conformité</Eyebrow>
            <h1 className="mt-4 font-display text-[#0F2B5B] text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05]">
              Une plateforme <span className="text-[#0369A1]">conforme</span> aux référentiels nationaux
            </h1>
            <p className="mt-6 text-[16px] text-[#475569] leading-relaxed">
              Sécurité, hébergement HDS, RGPD, identito-vigilance, authentification des
              professionnels : MetaConnect est pensé pour répondre nativement aux exigences
              réglementaires du numérique en santé. Les échanges entre professionnels passent
              par la{" "}
              <Link to="/mssante" className="text-[#0369A1] underline underline-offset-2">messagerie sécurisée MSSanté</Link>,
              opérée dans l'Espace de Confiance MSSanté de l'ANS.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="mc-card p-7">
              <div className="text-[12px] uppercase tracking-[0.18em] text-[#0369A1]">Ecosystème connecté</div>
              <div className="mt-5">
                <EcosystemLogos />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 hex-soft">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl">
            <Eyebrow>Piliers</Eyebrow>
            <h2 className="mt-4 font-display text-[#0F2B5B] text-3xl lg:text-4xl font-semibold">
              6 piliers pour une conformité native
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p) => (
              <div key={p.title} className="mc-card p-7" data-testid={`pillar-${p.tag.toLowerCase().replace(/\s|·/g, '-')}`}>
                <div className="flex items-center justify-between">
                  <div className="grid place-items-center h-11 w-11 rounded-lg bg-[rgba(45,123,255,0.15)] border border-[rgba(127,179,255,0.28)] text-[#0369A1]">
                    {p.icon}
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[#475569]">{p.tag}</span>
                </div>
                <h3 className="mt-5 font-display text-[#0F2B5B] text-[18px]">{p.title}</h3>
                <p className="mt-2.5 text-[13.5px] text-[#475569] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[var(--mc-navy-900)]">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <h2 className="font-display text-[#0F2B5B] text-3xl lg:text-4xl">
            Une question sur la conformité ?
          </h2>
          <p className="mt-4 text-[#475569] max-w-2xl mx-auto">
            Échangez avec notre équipe pour évaluer la mise en conformité de votre projet.
          </p>
          <div className="mt-7">
            <Link to="/demo" className="btn-primary px-7 py-3.5 rounded-full text-[14px] font-medium inline-flex items-center gap-2">
              Planifier un échange <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Eyebrow({ children }) {
  return (
    <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#0369A1]">
      <span className="h-px w-7 bg-[var(--mc-blue-400)]" />
      {children}
    </div>
  );
}
